import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  Pencil,
  Trash2,
  BarChart3,
  Link2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  APP_LINK_DEFAULTS,
  createLink,
  deleteLink,
  emptyLinkForm,
  getLinkAnalytics,
  linkToForm,
  listLinks,
  marketingShortLinkUrl,
  toggleLinkActive,
  updateLink,
} from '../../services/urlManageService';

const typeBadgeClass = (type) =>
  type === 'app' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';

const osLabel = {
  ios: 'iOS',
  android: 'Android',
  desktop: 'Desktop',
  other: 'Other',
};

const UrlManage = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [analyticsLink, setAnalyticsLink] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(emptyLinkForm('app'));
  const [formError, setFormError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: links = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['urlManageLinks'],
    queryFn: listLinks,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['urlManageAnalytics', analyticsLink?.id],
    queryFn: () => getLinkAnalytics(analyticsLink.id),
    enabled: Boolean(analyticsLink?.id),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        created_by: currentUser?.email || currentUser?.name || null,
      };
      if (editingLink?.id) {
        return updateLink(editingLink.id, payload);
      }
      return createLink(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['urlManageLinks']);
      setModalOpen(false);
      setEditingLink(null);
      setForm(emptyLinkForm('app'));
      setFormError('');
    },
    onError: (error) => {
      setFormError(error.message || 'Failed to save link');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => toggleLinkActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries(['urlManageLinks']),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLink(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(['urlManageLinks']);
      setAnalyticsLink((current) => (current?.id === deletedId ? null : current));
    },
  });

  const stats = useMemo(() => {
    const totalClicks = links.reduce((sum, link) => sum + (Number(link.click_count) || 0), 0);
    const activeCount = links.filter((link) => link.is_active).length;
    return { totalLinks: links.length, activeCount, totalClicks };
  }, [links]);

  const openCreateModal = (type = 'app') => {
    setEditingLink(null);
    setForm(emptyLinkForm(type));
    setFormError('');
    setModalOpen(true);
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setForm(linkToForm(link));
    setFormError('');
    setModalOpen(true);
  };

  const handleCopy = async (code) => {
    const url = marketingShortLinkUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } catch {
      window.prompt('Copy this URL:', url);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 size={24} className="text-[#1B4F9C]" />
            URL Manage
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Smart app links and promo short URLs on app.roadserve.in/go/
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => openCreateModal('promo')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-sm hover:bg-purple-100"
          >
            <Plus size={16} />
            Promo Link
          </button>
          <button
            type="button"
            onClick={() => openCreateModal('app')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1B4F9C] text-white text-sm hover:bg-[#153E7A]"
          >
            <Plus size={16} />
            App Smart Link
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Total links</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.totalLinks}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Active links</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.activeCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Total clicks</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.totalClicks}</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Short URL</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Clicks</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Loading links...
                  </td>
                </tr>
              ) : links.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No links yet. Create an App Smart Link or Promo Link.
                  </td>
                </tr>
              ) : (
                links.map((link) => {
                  const shortUrl = marketingShortLinkUrl(link.code);
                  return (
                    <tr key={link.id} className="border-t border-gray-100 hover:bg-gray-50/70">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{link.title}</div>
                        <div className="text-xs text-gray-400">/{link.code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <a
                            href={shortUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#1B4F9C] hover:underline truncate max-w-[220px]"
                          >
                            {shortUrl.replace('https://', '')}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleCopy(link.code)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            title="Copy URL"
                          >
                            {copiedCode === link.code ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                          </button>
                          <a
                            href={shortUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            title="Open"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeBadgeClass(link.type)}`}>
                          {link.type === 'app' ? 'App' : 'Promo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{link.click_count || 0}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleMutation.mutate({ id: link.id, isActive: !link.is_active })}
                          className="inline-flex items-center gap-1 text-xs"
                          title={link.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {link.is_active ? (
                            <>
                              <ToggleRight size={18} className="text-green-600" />
                              <span className="text-green-700">Active</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft size={18} className="text-gray-400" />
                              <span className="text-gray-500">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setAnalyticsLink(link)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-[#1B4F9C]"
                            title="Analytics"
                          >
                            <BarChart3 size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(link)}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete link "${link.title}"?`)) {
                                deleteMutation.mutate(link.id);
                              }
                            }}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLink ? 'Edit Link' : form.type === 'app' ? 'Create App Smart Link' : 'Create Promo Link'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                  setEditingLink(null);
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form
              className="p-6 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
            >
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Summer campaign"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Custom code (optional)</span>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => updateField('code', e.target.value.toLowerCase())}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Auto-generated if blank"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Type</span>
                  <select
                    value={form.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    disabled={Boolean(editingLink)}
                  >
                    <option value="app">App smart link</option>
                    <option value="promo">Promo link</option>
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Preview URL</span>
                  <input
                    type="text"
                    readOnly
                    value={form.code ? marketingShortLinkUrl(form.code) : 'Code will be generated on save'}
                    className="mt-1 w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                  />
                </label>
              </div>

              {form.type === 'promo' ? (
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Destination URL</span>
                  <input
                    type="url"
                    value={form.default_url}
                    onChange={(e) => updateField('default_url', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="https://app.roadserve.in/membership"
                    required
                  />
                </label>
              ) : (
                <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                  <p className="text-sm font-medium text-[#1B4F9C]">App redirect settings</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-sm text-gray-700">iOS app scheme</span>
                      <input
                        type="text"
                        value={form.ios_scheme_url}
                        onChange={(e) => updateField('ios_scheme_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">iOS fallback URL</span>
                      <input
                        type="url"
                        value={form.ios_fallback_url}
                        onChange={(e) => updateField('ios_fallback_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android Play Store URL</span>
                      <input
                        type="url"
                        value={form.android_url}
                        onChange={(e) => updateField('android_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android scheme (optional)</span>
                      <input
                        type="text"
                        value={form.android_scheme_url}
                        onChange={(e) => updateField('android_scheme_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Leave blank for Play Store only"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android package</span>
                      <input
                        type="text"
                        value={form.android_package}
                        onChange={(e) => updateField('android_package', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Desktop URL</span>
                      <input
                        type="url"
                        value={form.desktop_url}
                        onChange={(e) => updateField('desktop_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Defaults: iOS tries {APP_LINK_DEFAULTS.ios_scheme_url}, Android goes to Play Store, desktop opens referral page.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM source</span>
                  <input
                    type="text"
                    value={form.utm_source}
                    onChange={(e) => updateField('utm_source', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="whatsapp"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM medium</span>
                  <input
                    type="text"
                    value={form.utm_medium}
                    onChange={(e) => updateField('utm_medium', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="social"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM campaign</span>
                  <input
                    type="text"
                    value={form.utm_campaign}
                    onChange={(e) => updateField('utm_campaign', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="summer2026"
                  />
                </label>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                />
                Active
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingLink(null);
                    setFormError('');
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isLoading}
                  className="px-4 py-2 rounded-lg bg-[#1B4F9C] text-white text-sm hover:bg-[#153E7A] disabled:opacity-60"
                >
                  {saveMutation.isLoading ? 'Saving...' : editingLink ? 'Update Link' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {analyticsLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Link Analytics</h2>
                <p className="text-sm text-gray-500">{analyticsLink.title} · /{analyticsLink.code}</p>
              </div>
              <button
                type="button"
                onClick={() => setAnalyticsLink(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {analyticsLoading ? (
                <p className="text-sm text-gray-500">Loading analytics...</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-xs text-gray-500">Total clicks</p>
                      <p className="text-xl font-semibold">{analytics?.total || 0}</p>
                    </div>
                    {Object.entries(osLabel).map(([key, label]) => (
                      <div key={key} className="rounded-lg border border-gray-200 p-3">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className="text-xl font-semibold">{analytics?.byOs?.[key] || 0}</p>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Clicks by day</h3>
                    {analytics?.byDay?.length ? (
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-left">
                            <tr>
                              <th className="px-3 py-2">Date</th>
                              <th className="px-3 py-2">Clicks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.byDay.map((row) => (
                              <tr key={row.day} className="border-t border-gray-100">
                                <td className="px-3 py-2">{row.day}</td>
                                <td className="px-3 py-2 font-medium">{row.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No clicks recorded yet.</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Recent clicks</h3>
                    {analytics?.recent?.length ? (
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-left">
                            <tr>
                              <th className="px-3 py-2">Time</th>
                              <th className="px-3 py-2">OS</th>
                              <th className="px-3 py-2">Target</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.recent.map((row) => (
                              <tr key={row.id} className="border-t border-gray-100">
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.occurred_at ? new Date(row.occurred_at).toLocaleString() : '—'}
                                </td>
                                <td className="px-3 py-2">{osLabel[row.os] || row.os}</td>
                                <td className="px-3 py-2 truncate max-w-[260px]" title={row.resolved_target || ''}>
                                  {row.resolved_target || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No recent clicks.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrlManage;
